/**
 * LightShafts — the volumetric blades of sun that break through the surface (SPEC §10.3).
 * At a 62-degree pitch the water surface itself is never in frame, so these shafts are the
 * only direct evidence of a ceiling: they are what turns a flat arena into a water column.
 *
 * One draw call. Every shaft is a quad in a single buffer, billboarded around Y in the
 * vertex shader so it always presents its face to the camera without any CPU work.
 */
import * as THREE from 'three';
import { OCEAN, WATER } from '../data/tuning.ts';
import { Rng } from '../core/Rng.ts';
import { hexToLinear } from '../voxel/Palette.ts';
import { applyEnv, type SkyEnv } from '../world/DayNight.ts';

/** brightness at full noon; the day cycle scales this down and out */
const BASE_INTENSITY = 0.42;

const VERT = /* glsl */ `
uniform float uTime;
uniform float uSway;
uniform vec3  uOrigin;      // the shafts ride with the player so the column never runs out

attribute vec3  aCenter;    // shaft base position, relative to uOrigin
attribute vec4  aParams;    // x: half-width, y: height, z: phase, w: sway rate
attribute vec2  aCorner;    // (-1|1, 0|1) — local quad corner

varying vec2  vUv;
varying float vDepth;
varying float vSeed;

void main() {
  float halfW = aParams.x;
  float height = aParams.y;
  float phase  = aParams.z;
  float rate   = aParams.w;

  vec3 base = aCenter + uOrigin;
  // Lean the whole blade with a slow swing. Real shafts pivot at the surface, so the sway
  // is applied as a lateral offset scaled by height rather than a rotation about the base.
  float swing = sin(uTime * rate + phase) * uSway;

  vec3 toCam = cameraPosition - base;
  toCam.y = 0.0;
  vec3 right = normalize(vec3(-toCam.z, 0.0, toCam.x));   // perpendicular to the view, in XZ

  vec3 pos = base;
  pos += right * (aCorner.x * halfW);
  pos.y += aCorner.y * height;
  pos += right * (swing * aCorner.y * height);

  vec4 mv = viewMatrix * vec4(pos, 1.0);
  vUv = vec2(aCorner.x, aCorner.y);
  vDepth = -mv.z;
  vSeed = phase;
  gl_Position = projectionMatrix * mv;
}
`;

const FRAG = /* glsl */ `
uniform vec3  uSunColor;
uniform float uIntensity;
uniform float uFogDensity;
uniform float uTime;

varying vec2  vUv;
varying float vDepth;
varying float vSeed;

void main() {
  // Across the blade: squared falloff, so the core is bright and the edges dissolve rather
  // than ending on a visible seam.
  float across = 1.0 - clamp(abs(vUv.x), 0.0, 1.0);
  across *= across;

  // Along the blade: fades in under the surface and out before it reaches the sand, so a
  // shaft never terminates on a hard line at either end.
  float h = clamp(vUv.y, 0.0, 1.0);
  float along = smoothstep(0.0, 0.30, h) * (1.0 - smoothstep(0.55, 1.0, h));

  // A slow flicker per shaft, from the surface chop overhead.
  float flicker = 0.78 + 0.22 * sin(uTime * 0.7 + vSeed * 3.1);

  // Distance fade: shafts must not survive into the fog or they read as painted-on streaks.
  float fd = vDepth * uFogDensity;
  float reach = exp(-fd * fd);

  float a = across * along * flicker * reach * uIntensity;
  gl_FragColor = vec4(uSunColor * a, a);
}
`;

export class LightShafts {
  readonly mesh: THREE.Mesh;

  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.ShaderMaterial;
  private readonly origin: THREE.Vector3;

  constructor(seed = 0x5af75, count = OCEAN.shaftCount) {
    const rng = new Rng(seed >>> 0);

    const verts = count * 6;
    const center = new Float32Array(verts * 3);
    const params = new Float32Array(verts * 4);
    const corner = new Float32Array(verts * 2);

    // Two triangles per quad, corners in (x = -1|1, y = 0|1).
    const CX = [-1, 1, 1, -1, 1, -1];
    const CY = [0, 0, 1, 0, 1, 1];

    for (let i = 0; i < count; i++) {
      // Scattered on a ring rather than uniformly: shafts directly on top of the player wash
      // out the shark, and shafts beyond the fog line are wasted fill.
      const ang = rng.range(0, Math.PI * 2);
      const rad = rng.range(14, 62);
      const cx = Math.cos(ang) * rad;
      const cz = Math.sin(ang) * rad;
      const halfW = OCEAN.shaftWidth * rng.range(0.55, 1.25) * 0.5;
      const height = OCEAN.surfaceHeight * rng.range(0.9, 1.15);
      const phase = rng.range(0, Math.PI * 2);
      const rate = rng.range(0.10, 0.26);

      for (let v = 0; v < 6; v++) {
        const o = (i * 6 + v) * 3;
        center[o] = cx;
        center[o + 1] = 0;
        center[o + 2] = cz;
        const q = (i * 6 + v) * 4;
        params[q] = halfW;
        params[q + 1] = height;
        params[q + 2] = phase;
        params[q + 3] = rate;
        const c = (i * 6 + v) * 2;
        corner[c] = CX[v];
        corner[c + 1] = CY[v];
      }
    }

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('aCenter', new THREE.BufferAttribute(center, 3));
    this.geometry.setAttribute('aParams', new THREE.BufferAttribute(params, 4));
    this.geometry.setAttribute('aCorner', new THREE.BufferAttribute(corner, 2));
    // Everything is placed in the vertex shader, so `position` exists only to satisfy three's
    // draw-range bookkeeping; the bounding sphere is set by hand and culling turned off.
    this.geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(verts * 3), 3));

    this.origin = new THREE.Vector3();

    this.material = new THREE.ShaderMaterial({
      // Named so a compile failure says WHICH shader; three logs the material name and
      // nothing else identifying, and an empty name means reading every shader in the project.
      name: 'LightShafts',
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uTime: { value: 0 },
        uSway: { value: OCEAN.shaftSway },
        uOrigin: { value: this.origin },
        // Named uSunColor, not uColor, so applyEnv finds it and the shafts take the key
        // light's own colour — warm at noon, orange at dusk, blue if any survive the night.
        uSunColor: { value: new THREE.Vector3(...hexToLinear(WATER.sunColor)) },
        uIntensity: { value: BASE_INTENSITY },
        uFogDensity: { value: WATER.fogDensity * 0.72 },
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: true,
      side: THREE.DoubleSide,
      fog: false,
    });

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.name = 'light-shafts';
    this.mesh.frustumCulled = false;
    // Drawn after the opaque pass so the shafts lay over the sand, and before the snow.
    this.mesh.renderOrder = 5;
  }

  /** Anchor the shaft field on the player, snapped so the shafts do not slide with the camera. */
  update(time: number, focusX: number, focusZ: number): void {
    const step = 24;
    this.origin.set(Math.round(focusX / step) * step, 0, Math.round(focusZ / step) * step);
    this.material.uniforms.uTime.value = time;
  }


  /**
   * The day cycle drives this material's lighting; see world/DayNight.ts.
   *
   * `env.shafts` was being computed every frame and read by NOBODY: applyEnv matches on
   * uniform names and this material had none it recognised beyond the fog, so full-strength
   * midday sunbeams went on blazing through the moonlight. Consuming it here is the fix.
   *
   * Raised to a power rather than used straight. These are ADDITIVE over a scene that is
   * itself going dark, so a shaft at ten per cent brightness still reads as a bright streak
   * against near-black water — a linear ramp keeps them plainly visible long after the sun
   * that would justify them has set. The exponent makes them leave with the light instead.
   */
  setEnv(env: SkyEnv): void {
    applyEnv(this.material.uniforms, env);
    this.material.uniforms.uIntensity.value =
      BASE_INTENSITY * Math.pow(Math.max(0, env.shafts), 1.8);
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
  }
}
